import type { ReactNode } from 'react';

import { Button } from '../ui/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/Dialog';
import { toast } from '../ui/Toaster';
import { ConfiguredForm } from './ConfiguredForm';
import { Form } from './Form';
import { FormSubmitError } from './FormSubmitError';
import type { FormConfig } from './types';
import { useForm, type FormErrors } from './useForm';

export type FormDialogProps<TValues, Ctx = void> = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  config: FormConfig<TValues, Ctx>;
  context?: Ctx;
  initial: TValues;
  validate?: (values: TValues) => FormErrors<TValues>;
  onSubmit: (values: TValues) => Promise<void>;
  submitLabel: string;
  pendingLabel: string;
  /** Extra guard on the submit button (beyond pending) — e.g. nothing to pick. */
  submitDisabled?: boolean;
  successToast?: { title: string; description?: string };
  contentClassName?: string;
};

/**
 * Generic create/edit dialog: owns `useForm` + the Dialog/footer chrome, renders
 * the page's `FormConfig` via `ConfiguredForm`. Pages pass config + initial +
 * validate + onSubmit and stay hook-free. On success: optional toast, close, reset.
 */
export function FormDialog<TValues, Ctx = void>({
  open,
  onOpenChange,
  title,
  description,
  config,
  context,
  initial,
  validate,
  onSubmit,
  submitLabel,
  pendingLabel,
  submitDisabled,
  successToast,
  contentClassName,
}: FormDialogProps<TValues, Ctx>) {
  const form = useForm<TValues>({
    initial,
    validate,
    onSubmit: async (values) => {
      await onSubmit(values);
      if (successToast)
        toast.success(successToast.title, { description: successToast.description });
      onOpenChange(false);
      form.reset();
    },
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
        if (!next) form.reset();
      }}
    >
      <DialogContent className={contentClassName ?? 'max-w-lg'}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>

        <Form form={form}>
          <ConfiguredForm form={form} config={config} context={context} />
          <FormSubmitError form={form} />
          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="secondary"
              onClick={() => onOpenChange(false)}
              disabled={form.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={form.isPending || submitDisabled}>
              {form.isPending ? pendingLabel : submitLabel}
            </Button>
          </DialogFooter>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
